import { useState, useEffect, useRef, useCallback } from 'react';

interface SabotageAnimationEvent {
  id: string;
  targetTeamId: string;
  sabotageName: string;
  sabotageIcon: string;
  effectType: string;
}

interface ActiveAnimation {
  id: string;
  targetTeamId: string;
  sabotageName: string;
  sabotageIcon: string;
  phase: 'drop' | 'explode' | 'done';
  targetRect: DOMRect | null;
}

interface PersistentBomb {
  id: string;
  sabotageName: string;
  sabotageIcon: string;
  effectType: string;
}

interface SabotageAnimationOverlayProps {
  animationQueue: SabotageAnimationEvent[];
  onAnimationComplete: (eventId: string) => void;
  activeSabotagesByTeam: Record<string, PersistentBomb[]>;
}

const BOMB_DROP_DURATION = 1200;
const EXPLOSION_DURATION = 800;

/** Add shake class to the target DOM element */
function shakeTargetElement(teamId: string) {
  const el =
    document.querySelector(`[data-team-id="${teamId}"][data-on-clock="true"]`) ||
    document.querySelector(`[data-team-id="${teamId}"]`);
  if (!el) return;
  el.classList.add('animate-sabotage-shake');
  setTimeout(() => el.classList.remove('animate-sabotage-shake'), 600);
}

const SabotageAnimationOverlay = ({
  animationQueue,
  onAnimationComplete,
}: SabotageAnimationOverlayProps) => {
  const [animations, setAnimations] = useState<ActiveAnimation[]>([]);
  const processingRef = useRef(new Set<string>());

  const getTargetRect = useCallback((teamId: string): DOMRect | null => {
    const timerEl = document.querySelector(`[data-team-id="${teamId}"][data-on-clock="true"]`);
    if (timerEl) return timerEl.getBoundingClientRect();
    const orderEl = document.querySelector(`[data-team-id="${teamId}"]`);
    if (orderEl) return orderEl.getBoundingClientRect();
    return null;
  }, []);

  useEffect(() => {
    for (const event of animationQueue) {
      if (processingRef.current.has(event.id)) continue;
      processingRef.current.add(event.id);

      const targetRect = getTargetRect(event.targetTeamId);

      setAnimations(prev => [...prev, {
        id: event.id,
        targetTeamId: event.targetTeamId,
        sabotageName: event.sabotageName,
        sabotageIcon: event.sabotageIcon,
        phase: 'drop',
        targetRect,
      }]);

      // Phase 2: explosion + shake
      setTimeout(() => {
        setAnimations(prev =>
          prev.map(a => a.id === event.id ? { ...a, phase: 'explode' as const } : a)
        );
        shakeTargetElement(event.targetTeamId);
      }, BOMB_DROP_DURATION);

      // Phase 3: done
      setTimeout(() => {
        setAnimations(prev => prev.filter(a => a.id !== event.id));
        processingRef.current.delete(event.id);
        onAnimationComplete(event.id);
      }, BOMB_DROP_DURATION + EXPLOSION_DURATION);
    }
  }, [animationQueue, getTargetRect, onAnimationComplete]);

  if (animations.length === 0) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 pointer-events-none">
        {animations.map((anim) => {
          const targetX = anim.targetRect
            ? anim.targetRect.left + anim.targetRect.width / 2
            : window.innerWidth / 2;
          const targetY = anim.targetRect
            ? anim.targetRect.top + anim.targetRect.height / 2
            : window.innerHeight / 2;

          return (
            <div key={anim.id}>
              {anim.phase === 'drop' && (
                <img
                  src="/images/bomb.png"
                  alt="Bomb"
                  className="absolute animate-bomb-drop"
                  style={{
                    width: 80,
                    height: 80,
                    left: targetX - 40,
                    top: targetY - 40,
                  }}
                />
              )}

              {anim.phase === 'explode' && (
                <img
                  src="/images/explosion.png"
                  alt="Explosion"
                  className="absolute animate-explosion"
                  style={{
                    width: 120,
                    height: 120,
                    left: targetX - 60,
                    top: targetY - 60,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Screen flash on explosion */}
      {animations.some(a => a.phase === 'explode') && (
        <div className="fixed inset-0 z-[49] pointer-events-none animate-screen-flash" />
      )}
    </>
  );
};

/**
 * Persistent bomb icons rendered inline in each Draft Order team row.
 */
export const TeamSabotageBombs = ({
  teamId,
  activeSabotagesByTeam,
}: {
  teamId: string;
  activeSabotagesByTeam: Record<string, PersistentBomb[]>;
}) => {
  const bombs = activeSabotagesByTeam[teamId];
  if (!bombs || bombs.length === 0) return null;

  const visibleBombs = bombs.slice(0, 3);
  const totalCount = bombs.length;

  return (
    <div className="flex items-center shrink-0 relative">
      {visibleBombs.map((bomb, i) => (
        <div
          key={bomb.id}
          className="relative group"
          style={{ marginLeft: i > 0 ? -8 : 0, zIndex: 3 - i }}
        >
          <img
            src="/images/bomb.png"
            alt={bomb.sabotageName}
            className="w-[22px] h-[22px] drop-shadow-lg"
            style={{ transform: `rotate(${(i - 1) * 12}deg)` }}
          />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded bg-background/95 border border-border text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
            <span>{bomb.sabotageIcon} {bomb.sabotageName}</span>
          </div>
        </div>
      ))}
      {totalCount > 3 && (
        <span className="ml-0.5 text-[9px] font-bold text-damage bg-damage/20 rounded-full w-4 h-4 flex items-center justify-center border border-damage/40">
          {totalCount}
        </span>
      )}
      {totalCount <= 3 && totalCount > 1 && (
        <span className="ml-0.5 text-[9px] font-bold text-damage/80">
          ×{totalCount}
        </span>
      )}
    </div>
  );
};

export default SabotageAnimationOverlay;
